'use client'

import { useRef, useState } from 'react'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { IApp } from './app.types'
import { cn } from '@/lib/utils'
import { Crown, X } from 'lucide-react'
import { GradientButton } from '../common/gradient-button'
import {
  FormilySchemaRenderer,
  type IFormilyRendererRef,
} from '@/components/formily/renderer'
import { useDynamicI18n } from '@/hooks/use-dynamic-i18n'
import { useTranslations } from 'next-intl'

export const AppShowContainer = ({
  app,
  defaultValues,
  onSubmit,
  onDelete,
  onClose,
}: {
  app: IApp
  defaultValues?: Record<string, any>
  onSubmit?: (data: FormData) => void
  onDelete?: () => void
  onClose?: () => void
}) => {
  const formRef = useRef<IFormilyRendererRef>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const { dt } = useDynamicI18n()
  const t = useTranslations('apps-feature')

  const submitHandler = () => {
    if (!formRef.current) return
    const form = formRef.current?.form

    form
      .submit()
      .then((values) => {
        onSubmit?.(values as FormData)
      })
      .catch((err) => {
        console.log(err)
      })
  }

  const handleDeleteClick = () => {
    setShowDeleteDialog(true)
  }

  const handleDeleteConfirm = () => {
    onDelete?.()
    setShowDeleteDialog(false)
  }

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false)
  }

  if (showDeleteDialog) {
    return (
      <DialogContent className="max-w-sm p-0 bg-transparent border-0">
        <Container>
          <DialogHeader hidden>
            <DialogTitle hidden>{t('modal.delete_confirmation')}</DialogTitle>
          </DialogHeader>
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-2xl border border-gray-700">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-white">
                {t('modal.delete_super_prompt')}
              </h2>
            </div>
            <p className="text-white/80 mb-6 text-sm leading-relaxed">
              {t('modal.delete_description')}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors text-sm"
              >
                {t('modal.cancel')}
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors text-sm"
              >
                {t('modal.delete')}
              </button>
            </div>
          </div>
        </Container>
      </DialogContent>
    )
  }

  return (
    <div className="flex h-full w-full flex-col md:flex-row overflow-hidden bg-[#212121]">
      {/* Left section with white overlay */}
      <div className="flex-1 bg-white/[0.06] flex flex-col overflow-hidden">
        <div className="px-5 py-8 flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4 flex-shrink-0">
            <div className="flex items-center justify-center gap-2">
              {app.logo ? (
                <img
                  src={app.logo}
                  alt={app.name}
                  className="h-10 text-white rounded-md p-1"
                />
              ) : (
                <div className="h-8 w-12 text-white rounded-md p-1 bg-gray-800 flex items-center justify-center text-xs">
                  {t('modal.logo')}
                </div>
              )}
              <span className="text-white text-xl font-semibold">
                {app.name}
              </span>
            </div>
          </div>

          <p className="text-white text-base mb-6 font-normal leading-relaxed flex-shrink-0">
            {dt(app.description)}
          </p>

          {/* Preview area */}
          <div className="w-full flex-1 flex flex-col justify-start min-h-0">
            {app.example ? (
              <div className="flex flex-col h-full">
                <div className="flex-1 flex items-start justify-start min-h-0">
                  <img
                    src={app.example}
                    alt={app.name}
                    className="w-full h-auto max-h-full object-cover object-left rounded-lg"
                  />
                </div>
                <p className="text-gray-500 text-xs text-left italic w-full mt-3 flex-shrink-0">
                  {t('just_a_sample_image')}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Right section - Form */}
      <div className="w-full md:w-2/5 border-t md:border-t-0 md:border-l border-gray-800 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto px-5 py-8">
          <FormilySchemaRenderer
            defaultValues={defaultValues}
            formilySchema={app?.parameters?.prompt}
            needTranslation={true}
            ref={formRef}
          />
        </div>
        <div className="px-5 py-6 flex justify-end border-t border-gray-800 flex-shrink-0">
          <div className="flex gap-3 min-w-0 flex-shrink-0">
            <button
              onClick={handleDeleteClick}
              className="px-4 py-3 bg-transparent border border-white/20 hover:bg-white/10 text-white rounded-lg transition-colors whitespace-nowrap"
            >
              Delete
            </button>
            <button
              onClick={submitHandler}
              className="px-4 py-3 bg-white hover:bg-white/90 text-black rounded-lg transition-colors font-medium whitespace-nowrap"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const Container = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <div
      className={cn(
        'relative rounded-xl overflow-hidden',
        'bg-[#212121]',
        className,
      )}
    >
      {children}
    </div>
  )
}

export const AppModal = ({
  app,
  defaultValues,
  debug = false,
  onSubmit,
  onDelete,
}: {
  app: IApp
  defaultValues?: Record<string, any>
  debug?: boolean
  onSubmit?: (data: FormData) => void
  onDelete?: () => void
}) => {
  const t = useTranslations('apps-feature.modal')

  const onSubmitHandler = async (data: FormData) => {
    onSubmit?.(data)
    if (debug) {
      return
    }
  }

  const onDeleteHandler = () => {
    onDelete?.()
  }

  return (
    <DialogContent className="w-[60vw] h-[60vh] min-h-[500px] max-w-none p-0 bg-transparent border-0">
      <Container>
        <DialogHeader hidden>
          <DialogTitle hidden>{t('title')}</DialogTitle>
        </DialogHeader>
        <AppShowContainer
          app={app}
          defaultValues={defaultValues}
          onSubmit={onSubmitHandler}
          onDelete={onDeleteHandler}
        />
      </Container>
    </DialogContent>
  )
}
